import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { catchError, endWith, map, of, switchMap, takeUntil, withLatestFrom } from 'rxjs'

import { GenerateProcessRequest } from '@flowx/designer/modules/new-ai/agents/analyst/api/analyst.api'
import { AiAnalystUtils } from '@flowx/designer/modules/new-ai/agents/analyst/services/analyst.service'
import { AiAuditorApi } from '@flowx/designer/modules/new-ai/agents/auditor/api/auditor.api'
import {
  selectAppId,
  selectWorkspaceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'

import { AiAuditorActions } from './auditor.actions'

// eslint-disable-next-line import/no-cycle
import { AiActions } from '../../../store/ai.actions'
import { AiSelectors } from '../../../store/ai.selectors'

@Injectable()
export class AiAuditorEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #aiAuditorApiService = inject(AiAuditorApi)

  extractComplianceFromDocument = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAuditorActions.extractComplianceFromDocument),
      switchMap(({ documentContextReference, context }) =>
        this.#aiAuditorApiService
          .extractComplianceFromDocument(documentContextReference, context)
          .pipe(takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))))
      ),
      map((response) => {
        this.#store.dispatch(AiAuditorActions.extractComplianceFromDocumentSuccess({ response }))
        if (response.type_of_action === 'compliance_check_ui') {
          return AiAuditorActions.checkUI({ prompt: response.response })
        } else {
          return AiAuditorActions.checkProcess({ prompt: response.response })
        }
      }),
      catchError(({ status, error }) =>
        of(
          AiAuditorActions.extractComplianceFromDocumentFailure({
            statusCode: status,
            errorMsg: error,
          })
        )
      )
    )
  )

  checkProcess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAuditorActions.checkProcess),
      withLatestFrom(
        this.#store.select(AiSelectors.generatedProcess),
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId)
      ),
      map(
        ([{ prompt }, generatedProcess, sessionId, requestId]) =>
          ({
            question: prompt,
            context: generatedProcess || AiAnalystUtils.getInitialProcessContext(),
            session_id: sessionId,
            request_id: requestId,
          }) satisfies GenerateProcessRequest
      ),
      switchMap((payload) =>
        this.#aiAuditorApiService.checkProcess(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiAuditorActions.checkProcessSuccess({ response })),
          catchError((error) => of(AiAuditorActions.checkProcessFailure({ error })))
        )
      )
    )
  )

  checkUI = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAuditorActions.checkUI),
      withLatestFrom(
        this.#store.select(AiSelectors.currentUI),
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId)
      ),
      map(([{ prompt }, currentUI, sessionId, requestId]) => ({
        question: prompt,
        context: currentUI,
        session_id: sessionId,
        request_id: requestId,
      })),
      switchMap((payload) =>
        this.#aiAuditorApiService.checkUI(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiAuditorActions.checkUISuccess({ response })),
          catchError(({ status, error }) =>
            of(AiAuditorActions.checkUIFailure({ statusCode: status, errorMsg: error }))
          )
        )
      )
    )
  )

  // ─── SSE — preferred path ────────────────────────────────────────────────
  // Streams from /auditor/chat. Each chunk is forwarded as
  // getAnswerEventStream so the chat panel can render incrementally; the
  // stream terminates with streamCompleted.

  checkProcessStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAuditorActions.checkProcessStream),
      withLatestFrom(
        this.#store.select(AiSelectors.generatedProcess),
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId),
        this.#store.select(selectAppId),
        this.#store.select(selectWorkspaceId)
      ),
      switchMap(([{ prompt }, generatedProcess, sessionId, requestId, appId, workspaceId]) =>
        this.#aiAuditorApiService
          .getSSECheckCompliance(
            {
              question: prompt,
              context: generatedProcess || AiAnalystUtils.getInitialProcessContext(),
              session_id: sessionId,
              request_id: requestId,
              // FlowX tenancy — sent in the body (not headers) so we
              // don't need to allowlist a new Fx-* header on every
              // cluster ingress. ai-agents reads these into state.metadata
              // and Observatory uses application_id as project_id.
              application_id: appId,
              workspace_id: workspaceId,
            } as GenerateProcessRequest & { application_id?: string; workspace_id?: string },
            'check_process'
          )
          .pipe(
            takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            map((response) => AiAuditorActions.getAnswerEventStream({ response })),
            catchError(({ status, error }) =>
              of(
                AiAuditorActions.checkProcessFailure({
                  error: { statusCode: status, errorMsg: error },
                })
              )
            ),
            endWith(AiAuditorActions.streamCompleted())
          )
      )
    )
  )

  checkUIStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAuditorActions.checkUIStream),
      withLatestFrom(
        this.#store.select(AiSelectors.currentUI),
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId),
        this.#store.select(selectAppId),
        this.#store.select(selectWorkspaceId)
      ),
      switchMap(([{ prompt }, currentUI, sessionId, requestId, appId, workspaceId]) =>
        this.#aiAuditorApiService
          .getSSECheckCompliance(
            {
              question: prompt,
              context: currentUI,
              session_id: sessionId,
              request_id: requestId,
              application_id: appId,
              workspace_id: workspaceId,
            },
            'check_ui'
          )
          .pipe(
            takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            map((response) => AiAuditorActions.getAnswerEventStream({ response })),
            catchError(({ status, error }) =>
              of(AiAuditorActions.checkUIFailure({ statusCode: status, errorMsg: error }))
            ),
            endWith(AiAuditorActions.streamCompleted())
          )
      )
    )
  )

  sendFeedback = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiAuditorActions.sendFeedback),
        switchMap(({ payload }) => this.#aiAuditorApiService.sendFeedback(payload))
      ),
    { dispatch: false }
  )
}
